package com.swetdot.swetgram.data.profile

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.toDomain
import com.swetdot.swetgram.domain.BlockedUser
import com.swetdot.swetgram.domain.Page
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.protocol.dto.BlockedUserPage
import io.ktor.client.request.parameter
import com.swetdot.swetgram.protocol.dto.BlockedUser as BlockedUserDto

/**
 * Blocking.
 *
 * DECISIONS §6: an existing chat stays visible after a block and can be deleted by hand, messages
 * to someone who blocked the sender are refused with a NEUTRAL error that does not reveal the
 * block, and the blocked account sees a limited profile. None of that is enforced here -- the
 * server owns it -- but it is why nothing in this file tries to hide a conversation locally or to
 * translate a `recipient_unavailable` into "you were blocked". Doing either would turn a
 * deliberately ambiguous answer into a certain one.
 */
class BlockRepository(
    private val api: ApiClient,
) {
    /**
     * `blockUser`. 204.
     *
     * Answers 404 for an account that does not exist, which surfaces as
     * [ApiError.NotFound][com.swetdot.swetgram.data.api.ApiError.NotFound].
     */
    suspend fun block(userId: UserId) {
        api.post<Unit>("users/${userId.value}/block")
    }

    /**
     * `unblockUser`. 204.
     *
     * The contract enumerates no 404 here, so this succeeds whether or not a block existed --
     * which is also the only safe shape: a 404 would confirm the existence of an arbitrary user
     * id to anyone who cared to ask.
     */
    suspend fun unblock(userId: UserId) {
        api.delete<Unit>("users/${userId.value}/block")
    }

    /**
     * `listBlockedUsers`, cursor-paginated.
     *
     * A null [cursor] asks for the first page and sends no `cursor` parameter at all; pass
     * [Page.nextCursor] back to continue. The cursor is opaque -- it is the server's own encoding
     * and must never be parsed, compared or persisted across a sign-out.
     */
    suspend fun blockedUsers(cursor: String? = null, limit: Int = PAGE_SIZE): Page<BlockedUser> {
        val page: BlockedUserPage = api.get("users/me/blocked-users") {
            // Ktor drops a null parameter, so the first page carries no cursor.
            parameter("cursor", cursor)
            // The contract bounds this at 1..100. Clamping turns a caller's bad number into the
            // nearest legal page instead of a 400 on a list that is already on screen.
            parameter("limit", limit.coerceIn(MIN_LIMIT, MAX_LIMIT))
        }
        return Page(
            items = page.items.map { it.toDomain() },
            nextCursor = page.pageInfo.nextCursor,
            hasMore = page.pageInfo.hasMore,
        )
    }

    private companion object {
        /** DECISIONS §5 uses 30 as the standard first page, and the contract's default matches. */
        const val PAGE_SIZE = 30
        const val MIN_LIMIT = 1
        const val MAX_LIMIT = 100
    }
}

/**
 * The nested profile is a `UserPublic` the server has already projected: bio, online state and
 * last seen come back empty for someone who has been blocked. So a row here shows a name and an
 * avatar and nothing that implies live presence.
 */
private fun BlockedUserDto.toDomain(): BlockedUser = BlockedUser(
    user = user.toDomain(),
    // Kept rather than dropped when unparseable: this list is the only place the block can be
    // undone, so a row must survive a date the client cannot read.
    blockedAt = parseTimestamp(blockedAt),
)
